import type {IncentiveTier} from 'Types/incentiveTier/incentiveTierTypes';
import {TierType} from 'Types/incentiveTier/incentiveTierTypes';
import {DateTime} from 'luxon';
import {centsToString} from 'Utility/formatters/currency';

export const shouldShowSubtitleList: TierType[] = [
  TierType.TIME,
  TierType.VOLUME,
  TierType.LOYALTY,
  TierType.FREE_SHARES,
  TierType.TIME_LOYALTY,
  TierType.VOLUME_LOYALTY,
  TierType.TIME_VOLUME,
  TierType.FREE_SHARES_CODES,
];

export const tierSubtitleIcon: Record<TierType, string> = {
  [TierType.TIME]: 'clock_loader_10',
  [TierType.VOLUME]: 'stacks',
  [TierType.LOYALTY]: 'communities',
  [TierType.FREE_SHARES]: 'communities',
  [TierType.TIME_LOYALTY]: 'clock_loader_10',
  [TierType.VOLUME_LOYALTY]: 'stacks',
  [TierType.TIME_VOLUME]: 'clock_loader_10',
  [TierType.FREE_SHARES_CODES]: 'pin',
};

export const tierBadgeText: Record<TierType, string> = {
  [TierType.TIME]: 'Time',
  [TierType.VOLUME]: 'Volume',
  [TierType.LOYALTY]: 'Loyalty',
  [TierType.FREE_SHARES]: 'Loyalty',
  [TierType.TIME_LOYALTY]: 'Time Loyalty',
  [TierType.VOLUME_LOYALTY]: 'Volume Loyalty',
  [TierType.TIME_VOLUME]: 'Time Volume',
  [TierType.FREE_SHARES_CODES]: 'Consumable codes',
};

export const tierListIcon: Record<TierType, string> = {
  [TierType.TIME]: 'check_circle',
  [TierType.VOLUME]: 'check_circle',
  [TierType.LOYALTY]: 'check_circle',
  [TierType.FREE_SHARES]: 'check_circle',
  [TierType.TIME_LOYALTY]: 'check_circle',
  [TierType.VOLUME_LOYALTY]: 'check_circle',
  [TierType.TIME_VOLUME]: 'check_circle',
  [TierType.FREE_SHARES_CODES]: 'check_circle',
};

export const getTierTitle = (tier: IncentiveTier): string => {
  if (tier.minimumAmount) {
    switch (tier.tierType) {
      case TierType.VOLUME:
      case TierType.VOLUME_LOYALTY:
      case TierType.TIME_VOLUME:
        return `Invest $${centsToString(tier.minimumAmount, {
          removeZeroCents: true,
        })}`;
    }
  }

  switch (tier.tierType) {
    case TierType.TIME:
    case TierType.TIME_LOYALTY:
      return 'Invest by';
    case TierType.TIME_VOLUME:
      return tier.endAt ? 'Invest by' : formatTierType(tier.tierType);
    case TierType.LOYALTY:
      return 'Loyalty';
    case TierType.FREE_SHARES:
      return 'Cohort';
    case TierType.FREE_SHARES_CODES:
      return 'Consumable code';
    default:
      return formatTierType(tier.tierType);
  }
};

export const getTierSubtitle = (
  tier: IncentiveTier,
  formatDate = 'd MMM'
): string => {
  if (tier.minimumAmount) {
    switch (tier.tierType) {
      case TierType.TIME_VOLUME:
        return '+';
    }
  }

  if (tier.endAt) {
    switch (tier.tierType) {
      case TierType.TIME:
      case TierType.TIME_LOYALTY:
      case TierType.TIME_VOLUME:
        return `${formatDateTime(tier.endAt, formatDate)}`;
    }
  }

  switch (tier.tierType) {
    case TierType.TIME:
    case TierType.LOYALTY:
    case TierType.TIME_LOYALTY:
    case TierType.TIME_VOLUME:
      return '';
    case TierType.VOLUME:
    case TierType.VOLUME_LOYALTY:
      return '+';
    case TierType.FREE_SHARES:
    case TierType.FREE_SHARES_CODES:
    default:
      return 'offer';
  }
};

export const getTierListMessage = (
  tier: IncentiveTier,
  formatDate = 'd MMM'
): string => {
  const {tierType, minimumAmount, endAt} = tier;

  if (minimumAmount && endAt && tierType === TierType.TIME_VOLUME) {
    const formattedAmountStr = centsToString(minimumAmount, {
      removeZeroCents: true,
    });
    const formattedDateStr = formatDateTime(endAt, formatDate);
    return `Invest $${formattedAmountStr}+ by ${formattedDateStr} and receive:`;
  }

  if (minimumAmount) {
    switch (tierType) {
      case TierType.VOLUME:
      case TierType.VOLUME_LOYALTY:
      case TierType.TIME_LOYALTY:
      case TierType.TIME_VOLUME:
        return `Invest $${centsToString(minimumAmount, {
          removeZeroCents: true,
        })}+ and receive:`;
    }
  }

  if (endAt) {
    switch (tierType) {
      case TierType.TIME:
      case TierType.TIME_VOLUME:
        return `Invest by ${formatDateTime(endAt, formatDate)} and receive:`;
    }
  }

  switch (tierType) {
    case TierType.FREE_SHARES:
      return 'Get free shares and receive:';
    case TierType.FREE_SHARES_CODES:
      return 'Use your code and receive:';
    case TierType.TIME:
    case TierType.VOLUME:
    case TierType.LOYALTY:
    case TierType.TIME_LOYALTY:
    case TierType.VOLUME_LOYALTY:
    case TierType.TIME_VOLUME:
    default:
      return '';
  }
};

export const formatTierType = (tierType: TierType): string => {
  return tierType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
};

export const formatDateTime = (
  dateString: string | undefined,
  format = 'd MMM'
): string => {
  if (!dateString) return '';
  return DateTime.fromISO(dateString).toFormat(format);
};
